import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const SAVE_SUCCESS_FEEDBACK_MS = 3000

export type CampanhaRaspadinhaLayoutContextValue = {
  saveTrigger: (() => void) | null
  setSaveTrigger: (fn: (() => void) | null) => void
  isSubmitting: boolean
  setIsSubmitting: (value: boolean) => void
  hasUnsavedChanges: boolean
  setHasUnsavedChanges: (value: boolean) => void
  isSaveSuccessVisible: boolean
  reportSaveSuccess: () => void
}

const CampanhaRaspadinhaLayoutContext = createContext<
  CampanhaRaspadinhaLayoutContextValue | undefined
>(undefined)

export function CampanhaRaspadinhaLayoutProvider({ children }: { children: ReactNode }) {
  const [saveTrigger, setSaveTriggerState] = useState<(() => void) | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [saveSuccessUntil, setSaveSuccessUntil] = useState<number | null>(null)
  const [isSaveSuccessVisible, setIsSaveSuccessVisible] = useState(false)

  const setSaveTrigger = useCallback((fn: (() => void) | null) => {
    setSaveTriggerState(() => fn)
  }, [])

  const reportSaveSuccess = useCallback(() => {
    setSaveSuccessUntil(Date.now() + SAVE_SUCCESS_FEEDBACK_MS)
    setIsSaveSuccessVisible(true)
  }, [])

  useEffect(() => {
    if (!saveSuccessUntil) return

    const remainingMs = saveSuccessUntil - Date.now()
    if (remainingMs <= 0) {
      setIsSaveSuccessVisible(false)
      setSaveSuccessUntil(null)
      return
    }

    const timer = window.setTimeout(() => {
      setIsSaveSuccessVisible(false)
      setSaveSuccessUntil(null)
    }, remainingMs)

    return () => window.clearTimeout(timer)
  }, [saveSuccessUntil])

  useEffect(() => {
    if (hasUnsavedChanges && isSaveSuccessVisible) {
      setIsSaveSuccessVisible(false)
      setSaveSuccessUntil(null)
    }
  }, [hasUnsavedChanges, isSaveSuccessVisible])

  const value = useMemo(
    () => ({
      saveTrigger,
      setSaveTrigger,
      isSubmitting,
      setIsSubmitting,
      hasUnsavedChanges,
      setHasUnsavedChanges,
      isSaveSuccessVisible,
      reportSaveSuccess,
    }),
    [
      hasUnsavedChanges,
      isSaveSuccessVisible,
      isSubmitting,
      reportSaveSuccess,
      saveTrigger,
      setSaveTrigger,
    ]
  )

  return (
    <CampanhaRaspadinhaLayoutContext.Provider value={value}>
      {children}
    </CampanhaRaspadinhaLayoutContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCampanhaRaspadinhaLayout(): CampanhaRaspadinhaLayoutContextValue {
  const context = useContext(CampanhaRaspadinhaLayoutContext)
  if (!context) {
    throw new Error(
      'useCampanhaRaspadinhaLayout deve ser usado dentro de CampanhaRaspadinhaLayoutProvider'
    )
  }
  return context
}
